import React, { useState, useMemo } from 'react';
import { NotesRepositoryWidgetInstanceConfig, NotesRepositoryDataContent, RepositoryNote } from '../../../../../types';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import Button from '../../../../ui/Button';
import SearchBar from '../../../../ui/SearchBar';
import { WidgetEmptyState } from '../../../design';
import { useRepositoryItems, RepositoryModal, useWidgetPrefs } from '../../../blocks/repository';

interface NotesRepositoryWidgetRendererProps {
  config: NotesRepositoryWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: NotesRepositoryDataContent) => void;
  readOnly?: boolean;
}

// SVG icon components for reuse
const PlusIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CloseIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const NoteIcon = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);


const NotesRepositoryWidgetRenderer: React.FC<NotesRepositoryWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  // Repository CRUD, search, and copy-ref via shared hook
  const {
    items: notes, searchQuery, setSearchQuery,
    filteredItems, copiedId, handleCopyRef,
    createItem, updateItem, deleteItem,
    deletingItemId: deletingNoteId, setDeletingItemId: setDeletingNoteId,
    confirmDelete,
  } = useRepositoryItems<RepositoryNote>({
    config,
    dashboardData,
    onDataChange: onDataChange as ((dataKey: string, data: unknown) => void) | undefined,
    itemsKey: 'notes',
    idPrefix: 'note',
    searchFields: ['title', 'content', 'tags'],
  });

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const { showList, setShowList, sortBy, setSortBy } = useWidgetPrefs(config.dataKey);

  // Sort filtered notes — pinned always first
  const filteredNotes = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      switch (sortBy) {
        case 'title': return a.title.localeCompare(b.title);
        case 'created': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'updated': return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });
  }, [filteredItems, sortBy]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState<RepositoryNote | null>(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');

  const selectedNote = selectedNoteId
    ? notes.find(n => n.id === selectedNoteId)
    : null;

  const resetForm = () => {
    setNoteTitle('');
    setNoteContent('');
    setEditingNote(null);
  };

  const handleCreate = () => {
    if (!noteTitle.trim()) return;
    createItem({
      title: noteTitle.trim(),
      content: noteContent,
      tags: [],
    } as Omit<RepositoryNote, 'id' | 'createdAt' | 'updatedAt'>);
    setShowCreateModal(false);
    resetForm();
  };

  const handleDelete = (noteId: string) => {
    if (!onDataChange) return;
    deleteItem(noteId);
  };

  const confirmDeleteNote = () => {
    confirmDelete();
    setSelectedNoteId(null);
  };


  const handleEditSave = () => {
    if (!editingNote) return;
    updateItem(editingNote.id, {
      title: noteTitle.trim() || editingNote.title,
      content: noteContent,
    } as Partial<RepositoryNote>);
    setShowEditModal(false);
    resetForm();
  };

  const openEditModal = (note: RepositoryNote) => {
    setEditingNote(note);
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setShowEditModal(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        icon={NoteIcon}
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-neutral-50 flex items-center gap-3">
        {/* Search */}
        <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search..." className="flex-1" />

        {/* Count */}
        <p className="text-xs text-neutral-500 flex-shrink-0 ml-auto">
          {searchQuery
            ? `${filteredNotes.length} of ${notes.length} items`
            : `${notes.length} items`
          }
        </p>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'title' | 'created' | 'updated')}
          className="text-xs border border-neutral-200 rounded-lg px-2 h-9 bg-surface text-neutral-700 flex-shrink-0"
        >
          <option value="updated">Last Updated</option>
          <option value="created">Date Added</option>
          <option value="title">Title</option>
        </select>

        {/* View toggle */}
        <div className="flex border border-neutral-200 rounded-lg overflow-hidden flex-shrink-0 h-9">
          <button
            onClick={() => setShowList(true)}
            className={`px-2.5 flex items-center ${showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button
            onClick={() => setShowList(false)}
            className={`px-2.5 flex items-center ${!showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
          </button>
        </div>

        {/* Create Button */}
        {!readOnly && config.allowCreate !== false && (
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)} className="min-w-[90px] justify-center">
            New Note
          </Button>
        )}
      </div>

      {/* Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Notes List */}
        <div className={`${selectedNote ? 'w-1/3 border-r' : 'w-full'} overflow-auto`}>
          {filteredNotes.length === 0 ? (
            <WidgetEmptyState
              icon={NoteIcon}
              title={searchQuery ? 'No notes match your search' : 'No notes yet'}
              description={searchQuery ? 'Try a different search term' : (!readOnly && config.allowCreate !== false ? 'Click New Note to create your first note' : 'No notes have been added')}
              action={!searchQuery && !readOnly && config.allowCreate !== false ? <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>New Note</Button> : undefined}
            />
          ) : !showList && !selectedNote ? (
            /* Grid View - only when no note is selected */
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredNotes.map((note) => (
                <div
                  key={note.id}
                  className={`group relative rounded-xl cursor-pointer transition-all p-4 shadow-sm hover:shadow-lg ${
                    selectedNoteId === note.id
                      ? 'ring-2 ring-primary-500 shadow-lg shadow-primary-500/20'
                      : 'border border-neutral-200/60 hover:border-neutral-300'
                  }`}
                  onClick={() => setSelectedNoteId(note.id)}
                >
                  {/* Pin button — top left */}
                  <button
                    onClick={(e) => { e.stopPropagation(); updateItem(note.id, { isPinned: !note.isPinned } as Partial<RepositoryNote>); }}
                    className={`absolute top-1 left-1 p-1 rounded transition-all ${
                      note.isPinned ? 'text-warning-500 bg-black/70' : 'text-white bg-black/70 opacity-0 group-hover:opacity-100 hover:bg-black/90'
                    }`}
                    title={note.isPinned ? 'Unpin' : 'Pin to top'}
                  >
                    <svg className="w-3.5 h-3.5" fill={note.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                  </button>
                  {/* Copy ref button overlay */}
                  <button
                    onClick={(e) => handleCopyRef(note.id, e)}
                    className={`absolute top-1 right-1 p-1 rounded transition-all ${
                      copiedId === note.id
                        ? 'bg-success-500 text-white'
                        : 'bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-black/90'
                    }`}
                    title="Copy reference link"
                  >
                    {copiedId === note.id ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </button>
                  <p className="font-semibold text-neutral-800 truncate pr-8 mt-1">{note.title}</p>
                  <p className="text-sm text-neutral-600 mt-2 line-clamp-3 leading-relaxed">
                    {truncateContent(note.content, 80)}
                  </p>
                  <p className="text-xs text-neutral-400 mt-3 font-medium">{formatDate(note.updatedAt)}</p>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="divide-y divide-neutral-100">
              {filteredNotes.map((note) => (
                <div
                  key={note.id}
                  className={`group flex items-center gap-3 h-[72px] px-4 cursor-pointer transition-all ${
                    selectedNoteId === note.id
                      ? 'bg-primary-50 border-l-4 border-l-primary-500'
                      : 'hover:bg-neutral-50'
                  }`}
                  onClick={() => setSelectedNoteId(note.id)}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); updateItem(note.id, { isPinned: !note.isPinned } as Partial<RepositoryNote>); }}
                    className={`p-1 rounded flex-shrink-0 transition-all ${
                      note.isPinned ? 'text-warning-500' : 'text-neutral-300 opacity-0 group-hover:opacity-100 hover:text-warning-500'
                    }`}
                    title={note.isPinned ? 'Unpin' : 'Pin to top'}
                  >
                    <svg className="w-4 h-4" fill={note.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 truncate">{note.title}</p>
                    <p className="text-sm text-neutral-500 truncate mt-0.5">
                      {truncateContent(note.content, 60)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleCopyRef(note.id, e)}
                    className={`p-1.5 rounded flex-shrink-0 transition-all ${
                      copiedId === note.id ? 'text-success-600' : 'text-neutral-400 opacity-0 group-hover:opacity-100 hover:text-neutral-600'
                    }`}
                    title={copiedId === note.id ? 'Copied!' : 'Copy link'}
                  >
                    {copiedId === note.id
                      ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    }
                  </button>
                  <p className="text-xs text-neutral-400 flex-shrink-0">
                    {formatDate(note.updatedAt)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note Detail */}
        {selectedNote && (
          <div className="flex-1 flex flex-col overflow-hidden bg-surface">
            {/* Detail Header */}
            <div className="px-5 py-4 border-b border-neutral-200/80 bg-surface/80 backdrop-blur-sm flex items-center justify-between">
              <div className="flex-1 min-w-0">
                {!readOnly ? (
                  <input
                    type="text"
                    value={selectedNote.title}
                    onChange={(e) => updateItem(selectedNote.id, { title: e.target.value } as Partial<RepositoryNote>)}
                    className="font-semibold text-neutral-800 text-lg bg-transparent border-none outline-none w-full"
                  />
                ) : (
                  <h3 className="font-semibold text-neutral-800 text-lg truncate">{selectedNote.title}</h3>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={(e) => handleCopyRef(selectedNote.id, e)}
                  className={`p-2 rounded-lg transition-all ${
                    copiedId === selectedNote.id
                      ? 'text-success-600 bg-success-50'
                      : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
                  }`}
                  title={copiedId === selectedNote.id ? 'Copied!' : 'Copy reference link'}
                >
                  {copiedId === selectedNote.id ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  )}
                </button>
                {!readOnly && (
                  <>
                    {config.allowDelete !== false && (
                      <button
                        onClick={() => handleDelete(selectedNote.id)}
                        className="p-2 text-neutral-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={() => setSelectedNoteId(null)}
                  className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Note Content */}
            <div className="flex-1 overflow-auto p-5">
              {!readOnly ? (
                <textarea
                  value={selectedNote.content}
                  onChange={(e) => updateItem(selectedNote.id, { content: e.target.value } as Partial<RepositoryNote>)}
                  placeholder="Write your note here..."
                  className="w-full h-full text-sm text-neutral-800 bg-transparent border-none outline-none resize-none leading-relaxed placeholder-neutral-400"
                  autoFocus
                />
              ) : (
                <div className="prose prose-neutral prose-sm max-w-none whitespace-pre-wrap leading-relaxed">
                  {selectedNote.content || (
                    <span className="text-neutral-400 italic">No content</span>
                  )}
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="px-5 py-3 border-t border-neutral-100 bg-neutral-50/50 text-xs text-neutral-500 flex justify-between font-medium">
              <span>Created: {new Date(selectedNote.createdAt).toLocaleString()}</span>
              <span>Updated: {new Date(selectedNote.updatedAt).toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <RepositoryModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="New Note"
        onSave={handleCreate}
        saveLabel="Create Note"
        saveDisabled={!noteTitle.trim()}
        itemTitle={noteTitle}
        onTitleChange={setNoteTitle}
      >
        {/* Content textarea */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Content</label>
          <textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} placeholder="Write your note here..." rows={6} className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none" />
        </div>
      </RepositoryModal>

      {/* Edit Modal */}
      <RepositoryModal
        isOpen={showEditModal && editingNote !== null}
        onClose={() => { setShowEditModal(false); resetForm(); }}
        title="Edit Note"
        onSave={handleEditSave}
        saveLabel="Save Changes"
        itemTitle={noteTitle}
        onTitleChange={setNoteTitle}
      >
        {/* Content textarea */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Content</label>
          <textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} rows={6} className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none" />
        </div>
      </RepositoryModal>

      <ConfirmationModal
        isOpen={deletingNoteId !== null}
        onClose={() => setDeletingNoteId(null)}
        onAction={confirmDeleteNote}
        title="Delete Note"
        description="Are you sure you want to delete this note?"
        actionText="Delete"
      />
    </div>
  );
};

export default NotesRepositoryWidgetRenderer;
